export const PlayLaunchMode = {
  Embedded: 'embedded',
  NewWindow: 'new_window',
  MobilePreview: 'device_emulation',
} as const;

export type PlayLaunchMode = (typeof PlayLaunchMode)[keyof typeof PlayLaunchMode];

export const PlayOrientation = {
  Portrait: 'portrait',
  Landscape: 'landscape',
} as const;

export type PlayOrientation = (typeof PlayOrientation)[keyof typeof PlayOrientation];

export interface SafeAreaInsets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface PlayWindowConfig {
  width: number;
  height: number;
  devicePixelRatio: number;
  title: string;
  center: boolean;
  resizable: boolean;
}

export interface PlayMobilePreviewConfig {
  presetId: string;
  presetName: string;
  width: number;
  height: number;
  orientation: PlayOrientation;
  devicePixelRatio: number;
  userAgent: string;
  platform: string;
  mobileViewport: boolean;
  touchEmulation: boolean;
  showDeviceFrame: boolean;
  showNotchOverlay: boolean;
  safeAreaInsets: SafeAreaInsets;
}

export interface PlayPreviewConfig {
  launchMode: PlayLaunchMode;
  window: PlayWindowConfig;
  emulation: PlayMobilePreviewConfig;
}

export interface PlayDevicePreset {
  id: string;
  name: string;
  kind: 'desktop' | 'phone' | 'tablet';
  width: number;
  height: number;
  defaultOrientation: PlayOrientation;
  devicePixelRatio: number;
  userAgent: string;
  platform: string;
  mobileViewport: boolean;
  touchEmulation: boolean;
  safeAreaInsets: SafeAreaInsets;
  notch: boolean;
}

export const DEFAULT_SAFE_AREA_INSETS: SafeAreaInsets = {
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
};

export interface PlayWindowSizePreset {
  id: string;
  name: string;
  kind: 'desktop' | 'laptop' | 'tablet' | 'phone';
  width: number;
  height: number;
}

export const PLAY_WINDOW_SIZE_PRESETS: PlayWindowSizePreset[] = [
  { id: 'desktop-fhd', name: 'Desktop 1920 x 1080 (Full HD)', kind: 'desktop', width: 1920, height: 1080 },
  { id: 'desktop-qhd', name: 'Desktop 2560 x 1440 (QHD)', kind: 'desktop', width: 2560, height: 1440 },
  { id: 'desktop-4k', name: 'Desktop 3840 x 2160 (4K UHD)', kind: 'desktop', width: 3840, height: 2160 },
  { id: 'laptop-hd', name: 'Laptop 1366 x 768', kind: 'laptop', width: 1366, height: 768 },
  { id: 'laptop-fhd', name: 'Laptop 1920 x 1200', kind: 'laptop', width: 1920, height: 1200 },
  { id: 'tablet-ipad-landscape', name: 'Tablet 1194 x 834 (iPad Pro 11 Landscape)', kind: 'tablet', width: 1194, height: 834 },
  { id: 'tablet-ipad-portrait', name: 'Tablet 834 x 1194 (iPad Pro 11 Portrait)', kind: 'tablet', width: 834, height: 1194 },
  { id: 'tablet-galaxy-landscape', name: 'Tablet 1752 x 2800 (Galaxy Tab S10 Ultra Portrait)', kind: 'tablet', width: 1752, height: 2800 },
  { id: 'phone-iphone-portrait', name: 'Phone 402 x 874 (iPhone 16 Pro Portrait)', kind: 'phone', width: 402, height: 874 },
  { id: 'phone-iphone-landscape', name: 'Phone 874 x 402 (iPhone 16 Pro Landscape)', kind: 'phone', width: 874, height: 402 },
  { id: 'phone-pixel-portrait', name: 'Phone 412 x 915 (Pixel 9 Portrait)', kind: 'phone', width: 412, height: 915 },
  { id: 'phone-galaxy-portrait', name: 'Phone 393 x 852 (Galaxy S25 Portrait)', kind: 'phone', width: 393, height: 852 },
];

export const PLAY_DEVICE_PRESETS: PlayDevicePreset[] = [
  {
    id: 'iphone-se-3',
    name: 'iPhone SE (3rd Gen)',
    kind: 'phone',
    width: 375,
    height: 667,
    defaultOrientation: PlayOrientation.Portrait,
    devicePixelRatio: 2,
    userAgent: 'Mozilla/5.0 (iPhone; CPU iPhone OS 17_0 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/17.0 Mobile/15E148 Safari/604.1',
    platform: 'iPhone',
    mobileViewport: true,
    touchEmulation: true,
    safeAreaInsets: { ...DEFAULT_SAFE_AREA_INSETS },
    notch: false,
  },
  {
    id: 'iphone-16',
    name: 'iPhone 16',
    kind: 'phone',
    width: 393,
    height: 852,
    defaultOrientation: PlayOrientation.Portrait,
    devicePixelRatio: 3,
    userAgent: 'Mozilla/5.0 (iPhone; CPU iPhone OS 18_4 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/18.4 Mobile/15E148 Safari/604.1',
    platform: 'iPhone',
    mobileViewport: true,
    touchEmulation: true,
    safeAreaInsets: {
      top: 47,
      right: 0,
      bottom: 34,
      left: 0,
    },
    notch: true,
  },
  {
    id: 'iphone-16-pro',
    name: 'iPhone 16 Pro',
    kind: 'phone',
    width: 402,
    height: 874,
    defaultOrientation: PlayOrientation.Portrait,
    devicePixelRatio: 3,
    userAgent: 'Mozilla/5.0 (iPhone; CPU iPhone OS 18_4 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/18.4 Mobile/15E148 Safari/604.1',
    platform: 'iPhone',
    mobileViewport: true,
    touchEmulation: true,
    safeAreaInsets: {
      top: 59,
      right: 0,
      bottom: 34,
      left: 0,
    },
    notch: true,
  },
  {
    id: 'iphone-16-pro-max',
    name: 'iPhone 16 Pro Max',
    kind: 'phone',
    width: 440,
    height: 956,
    defaultOrientation: PlayOrientation.Portrait,
    devicePixelRatio: 3,
    userAgent: 'Mozilla/5.0 (iPhone; CPU iPhone OS 18_4 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/18.4 Mobile/15E148 Safari/604.1',
    platform: 'iPhone',
    mobileViewport: true,
    touchEmulation: true,
    safeAreaInsets: {
      top: 59,
      right: 0,
      bottom: 34,
      left: 0,
    },
    notch: true,
  },
  {
    id: 'iphone-16e',
    name: 'iPhone 16e',
    kind: 'phone',
    width: 393,
    height: 852,
    defaultOrientation: PlayOrientation.Portrait,
    devicePixelRatio: 3,
    userAgent: 'Mozilla/5.0 (iPhone; CPU iPhone OS 19_0 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/19.0 Mobile/15E148 Safari/604.1',
    platform: 'iPhone',
    mobileViewport: true,
    touchEmulation: true,
    safeAreaInsets: {
      top: 47,
      right: 0,
      bottom: 34,
      left: 0,
    },
    notch: true,
  },
  {
    id: 'pixel-9',
    name: 'Pixel 9',
    kind: 'phone',
    width: 412,
    height: 915,
    defaultOrientation: PlayOrientation.Portrait,
    devicePixelRatio: 2.75,
    userAgent: 'Mozilla/5.0 (Linux; Android 15; Pixel 9) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/136.0.0.0 Mobile Safari/537.36',
    platform: 'Android',
    mobileViewport: true,
    touchEmulation: true,
    safeAreaInsets: {
      top: 30,
      right: 0,
      bottom: 20,
      left: 0,
    },
    notch: true,
  },
  {
    id: 'pixel-9-pro-xl',
    name: 'Pixel 9 Pro XL',
    kind: 'phone',
    width: 448,
    height: 998,
    defaultOrientation: PlayOrientation.Portrait,
    devicePixelRatio: 3.0,
    userAgent: 'Mozilla/5.0 (Linux; Android 15; Pixel 9 Pro XL) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/136.0.0.0 Mobile Safari/537.36',
    platform: 'Android',
    mobileViewport: true,
    touchEmulation: true,
    safeAreaInsets: {
      top: 28,
      right: 0,
      bottom: 20,
      left: 0,
    },
    notch: true,
  },
  {
    id: 'galaxy-s25',
    name: 'Galaxy S25',
    kind: 'phone',
    width: 393,
    height: 852,
    defaultOrientation: PlayOrientation.Portrait,
    devicePixelRatio: 3,
    userAgent: 'Mozilla/5.0 (Linux; Android 16; SM-S931B) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/136.0.0.0 Mobile Safari/537.36',
    platform: 'Android',
    mobileViewport: true,
    touchEmulation: true,
    safeAreaInsets: {
      top: 30,
      right: 0,
      bottom: 20,
      left: 0,
    },
    notch: true,
  },
  {
    id: 'galaxy-z-fold6-cover',
    name: 'Galaxy Z Fold6 (Cover)',
    kind: 'phone',
    width: 393,
    height: 968,
    defaultOrientation: PlayOrientation.Portrait,
    devicePixelRatio: 3,
    userAgent: 'Mozilla/5.0 (Linux; Android 15; SM-F956B) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/136.0.0.0 Mobile Safari/537.36',
    platform: 'Android',
    mobileViewport: true,
    touchEmulation: true,
    safeAreaInsets: {
      top: 26,
      right: 0,
      bottom: 18,
      left: 0,
    },
    notch: false,
  },
  {
    id: 'galaxy-z-fold6-main',
    name: 'Galaxy Z Fold6 (Main)',
    kind: 'tablet',
    width: 904,
    height: 1008,
    defaultOrientation: PlayOrientation.Portrait,
    devicePixelRatio: 2.5,
    userAgent: 'Mozilla/5.0 (Linux; Android 15; SM-F956B) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/136.0.0.0 Mobile Safari/537.36',
    platform: 'Android',
    mobileViewport: true,
    touchEmulation: true,
    safeAreaInsets: {
      top: 18,
      right: 0,
      bottom: 12,
      left: 0,
    },
    notch: false,
  },
  {
    id: 'ipad-mini-7',
    name: 'iPad Mini (7th Gen)',
    kind: 'tablet',
    width: 744,
    height: 1133,
    defaultOrientation: PlayOrientation.Portrait,
    devicePixelRatio: 2,
    userAgent: 'Mozilla/5.0 (iPad; CPU OS 18_0 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/18.0 Mobile/15E148 Safari/604.1',
    platform: 'iPad',
    mobileViewport: true,
    touchEmulation: true,
    safeAreaInsets: {
      top: 24,
      right: 0,
      bottom: 20,
      left: 0,
    },
    notch: false,
  },
  {
    id: 'ipad-air-11-m3',
    name: 'iPad Air 11 (M3)',
    kind: 'tablet',
    width: 834,
    height: 1194,
    defaultOrientation: PlayOrientation.Portrait,
    devicePixelRatio: 2,
    userAgent: 'Mozilla/5.0 (iPad; CPU OS 18_4 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/18.4 Mobile/15E148 Safari/604.1',
    platform: 'iPad',
    mobileViewport: true,
    touchEmulation: true,
    safeAreaInsets: {
      top: 24,
      right: 0,
      bottom: 20,
      left: 0,
    },
    notch: false,
  },
  {
    id: 'ipad-pro-13-m4',
    name: 'iPad Pro 13 (M4)',
    kind: 'tablet',
    width: 1032,
    height: 1376,
    defaultOrientation: PlayOrientation.Portrait,
    devicePixelRatio: 2,
    userAgent: 'Mozilla/5.0 (iPad; CPU OS 18_4 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/18.4 Mobile/15E148 Safari/604.1',
    platform: 'iPad',
    mobileViewport: true,
    touchEmulation: true,
    safeAreaInsets: {
      top: 24,
      right: 0,
      bottom: 20,
      left: 0,
    },
    notch: false,
  },
  {
    id: 'galaxy-tab-s10-ultra',
    name: 'Galaxy Tab S10 Ultra',
    kind: 'tablet',
    width: 1752,
    height: 2800,
    defaultOrientation: PlayOrientation.Portrait,
    devicePixelRatio: 1.8,
    userAgent: 'Mozilla/5.0 (Linux; Android 15; SM-X920) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/136.0.0.0 Safari/537.36',
    platform: 'Android',
    mobileViewport: true,
    touchEmulation: true,
    safeAreaInsets: {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
    notch: false,
  },
  {
    id: 'pixel-tablet',
    name: 'Pixel Tablet',
    kind: 'tablet',
    width: 1600,
    height: 2560,
    defaultOrientation: PlayOrientation.Portrait,
    devicePixelRatio: 2,
    userAgent: 'Mozilla/5.0 (Linux; Android 15; Pixel Tablet) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/136.0.0.0 Safari/537.36',
    platform: 'Android',
    mobileViewport: true,
    touchEmulation: true,
    safeAreaInsets: { ...DEFAULT_SAFE_AREA_INSETS },
    notch: false,
  },
];

export const DEFAULT_PLAY_WINDOW_CONFIG: PlayWindowConfig = {
  width: 1280,
  height: 720,
  devicePixelRatio: 1,
  title: 'Play Mode',
  center: true,
  resizable: true,
};

const DEFAULT_DEVICE_PRESET = PLAY_DEVICE_PRESETS.find(preset => preset.id === 'iphone-16-pro') ?? PLAY_DEVICE_PRESETS[0];

export const DEFAULT_PLAY_MOBILE_PREVIEW_CONFIG: PlayMobilePreviewConfig = {
  presetId: DEFAULT_DEVICE_PRESET.id,
  presetName: DEFAULT_DEVICE_PRESET.name,
  width: DEFAULT_DEVICE_PRESET.width,
  height: DEFAULT_DEVICE_PRESET.height,
  orientation: DEFAULT_DEVICE_PRESET.defaultOrientation,
  devicePixelRatio: DEFAULT_DEVICE_PRESET.devicePixelRatio,
  userAgent: DEFAULT_DEVICE_PRESET.userAgent,
  platform: DEFAULT_DEVICE_PRESET.platform,
  mobileViewport: DEFAULT_DEVICE_PRESET.mobileViewport,
  touchEmulation: DEFAULT_DEVICE_PRESET.touchEmulation,
  showDeviceFrame: true,
  showNotchOverlay: DEFAULT_DEVICE_PRESET.notch,
  safeAreaInsets: { ...DEFAULT_DEVICE_PRESET.safeAreaInsets },
};

export const DEFAULT_PLAY_PREVIEW_CONFIG: PlayPreviewConfig = {
  launchMode: PlayLaunchMode.Embedded,
  window: { ...DEFAULT_PLAY_WINDOW_CONFIG },
  emulation: { ...DEFAULT_PLAY_MOBILE_PREVIEW_CONFIG },
};

export function getPlayDevicePresetById(presetId: string): PlayDevicePreset {
  return PLAY_DEVICE_PRESETS.find(preset => preset.id === presetId) ?? PLAY_DEVICE_PRESETS[0];
}

export function applyOrientationToSize(
  width: number,
  height: number,
  orientation: PlayOrientation,
): { width: number; height: number } {
  const isLandscape = orientation === PlayOrientation.Landscape;
  const longer = Math.max(width, height);
  const shorter = Math.min(width, height);

  if (isLandscape) {
    return { width: longer, height: shorter };
  }

  return { width: shorter, height: longer };
}

export function createDeviceConfigFromPreset(
  presetId: string,
  orientation?: PlayOrientation,
): PlayMobilePreviewConfig {
  const preset = getPlayDevicePresetById(presetId);
  const finalOrientation = orientation ?? preset.defaultOrientation;
  const size = applyOrientationToSize(preset.width, preset.height, finalOrientation);

  return {
    presetId: preset.id,
    presetName: preset.name,
    width: size.width,
    height: size.height,
    orientation: finalOrientation,
    devicePixelRatio: preset.devicePixelRatio,
    userAgent: preset.userAgent,
    platform: preset.platform,
    mobileViewport: preset.mobileViewport,
    touchEmulation: preset.touchEmulation,
    showDeviceFrame: true,
    showNotchOverlay: preset.notch,
    safeAreaInsets: { ...preset.safeAreaInsets },
  };
}
